import createBehavior from '@js/functions/createBehavior.js';
import lockScroll from '@js/utils/lock-scroll.js';
import * as focusTrap from 'focus-trap';
import { isBreakpoint } from '@area17/a17-helpers';

const headerBehavior = createBehavior(
  'header',
  {
    bindUI() {
      this.stickyClass = 'is-sticky';
      this.transformedClass = 'is-transformed';
      this.transitionClass = 'has-transition';
      this.isOpenClass = 'is-nav-open';
      this.isScrollingUp = false;

      this.barbaContainer = document.querySelector('[data-barba="container"]:last-child');

      this.navToggle = this.barbaContainer.querySelectorAll('[data-nav-toggle]');
      this.nav = this.barbaContainer.querySelector('[data-nav-menu]');
      this.navTitle = this.barbaContainer.querySelector('[data-nav-menutitle]');
      this.notification = this.barbaContainer.querySelector('.notification');
      this.navAnchor = this.barbaContainer.querySelector('[data-behavior="nav-anchor"]');

      // Add timeout to make sure we have the full page render correctly before calculate.
      setTimeout(() => {
        this.headerH = this.node.offsetHeight + 1;
        this.notificationHeight = this.notification ? this.notification.offsetHeight : 0;
        this.headerTop = this.node.getBoundingClientRect().top + window.scrollY + this.notificationHeight;
      }, 300);
    },
    bindEvents() {
      Array.from(this.navToggle).map(item => {
        item.addEventListener('click', this.toggleNav, false);
      });
      document.addEventListener('scrolled', this.scrollHandler, false)
      window.addEventListener('lockScroll:willBeEnable', this.lockScrollWillBeEnableHandler, false);
      window.addEventListener('lockScroll:disable', this.lockScrollDisableHandler, false);

      // Close nav when cart is opened
      document.addEventListener('miniCart:opened', this.miniCartOpened, false)

      // Add event listener for esc touch.
      document.addEventListener('keydown', this.keyboardHandler, false);
    },
    lockScrollWillBeEnableHandler() {
      this.isDisable = true;
    },
    lockScrollDisableHandler() {
      this.isDisable = false;

      // Show header.
      this.isScrollingUp = true;
      this.node.classList.add(this.transitionClass);
      this.node.classList.remove(this.transformedClass);
    },
    updateState() {
      if (isBreakpoint('xlarge+')) {
        if (document.body.classList.contains(this.isOpenClass)) {
          this.closeNav();
        }
        this.nav.removeAttribute('role');
        this.nav.removeAttribute('aria-modal');
        this.nav.removeAttribute('inert');
      } else {
        this.nav.setAttribute('role', 'dialog');
        this.nav.setAttribute('aria-modal', 'true');
        if (document.body.classList.contains(this.isOpenClass)) {
          this.nav.removeAttribute('inert');
        } else {
          this.nav.setAttribute('inert', 'true');
        }
      }
    },
    openNav() {
      if (!document.body.classList.contains(this.isOpenClass)) {
        document.dispatchEvent(new CustomEvent('nav:opened'));
        lockScroll.enable(350);
        this.nav.removeAttribute('inert');
        if (!this.focusTrap) {
          this.focusTrap = focusTrap.createFocusTrap(this.nav);
        }
        this.focusTrap.activate();
        this.navTitle.focus({preventScroll: true});
        document.body.classList.add(this.isOpenClass);
      }
    },
    closeNav() {
      if (document.body.classList.contains(this.isOpenClass)) {
        document.dispatchEvent(new CustomEvent('nav:closed'));
        lockScroll.disable();

        this.isDisable = false;
        this.nav.setAttribute('inert', '');
        this.focusTrap.deactivate();
        this.navToggle[0].focus();
        document.body.classList.remove(this.isOpenClass);
      }
    },
    toggleNav() {
      // If it's open, lock scroll and emit event
      if (!document.body.classList.contains(this.isOpenClass)) {
        this.openNav();
      } else {
        this.closeNav();
      }
    },
    scrollHandler(e) {
      // If it's disable, return.
      if (this.isDisable) return;

      // Update scroll direction.
      if (e.detail.last < e.detail.prev && !this.isScrollingUp) {
        this.isScrollingUp = true;
      } else if (e.detail.last > e.detail.prev && this.isScrollingUp) {
        this.isScrollingUp = false;
      }

      // Update header.
      this.updateHeader(e.detail.last);
    },
    miniCartOpened() {
      this.closeNav();
      this.isScrollingUp = true;
      this.updateHeader(window.pageY);
    },
    updateHeader(scrollTop) {
      const isMobile = window.innerWidth <= 599;
      if (isMobile) {

        if (this.isDisable) return;


          if (this.isScrollingUp) {
            this.node.classList.add('is-logo-visible');
            this.node.classList.remove('is-logo-hidden');
          } else {
            this.node.classList.add('is-logo-hidden');
            this.node.classList.remove('is-logo-visible');
          }

        if (scrollTop === 0) {
          this.node.classList.remove('is-logo-hidden', 'is-logo-visible');
        }
        
        return;
      }

      if (this.isScrollingUp) {

        // Add transition.
        this.node.classList.add(this.transitionClass);

        // Show header.
        this.node.classList.remove(this.transformedClass);
        this.navAnchor?.classList.add('with-visible-header');

        // If we reach the top, remove sticky.
        if (scrollTop === 0) {
          this.node.classList.remove(this.transitionClass);
          this.node.classList.remove(this.stickyClass);
          this.navAnchor?.classList.remove('with-visible-header');
        }

      } else {
        if (scrollTop >= this.headerTop + this.headerH ) {
          this.node.classList.add(this.stickyClass, this.transformedClass);
          this.navAnchor?.classList.remove('with-visible-header');
        }
      }
    },
    keyboardHandler(e) {
      // If it's not open, return.
      if (!document.body.classList.contains(this.isOpenClass)) {
        return;
      }

      // If we press escape.
      if (e.keyCode === 27) {
        this.closeNav();
      }
    },
    unbindEvents() {
      Array.from(this.navToggle).map(item => {
        item.removeEventListener('click', this.toggleNav);
      });
      document.removeEventListener('scrolled', this.scrollHandler)
      window.removeEventListener('lockScroll:willBeEnable', this.lockScrollWillBeEnableHandler);
      window.removeEventListener('lockScroll:disable', this.lockScrollDisableHandler);
      document.removeEventListener('miniCart:opened', this.miniCartOpened, false)
      document.removeEventListener('keydown', this.keyboardHandler);
    }
  },
  {
    init() {
      this.isDisable = false;

      this.bindUI();
      this.bindEvents();

      this.updateState();
    },
    mediaQueryUpdated() {
      this.updateState();
    },
    destroy() {
      this.closeNav();
      this.unbindEvents();
    }
  }
);

export default headerBehavior;
